// pms-adapters / booking — THE SIGNED QUOTE ID (D6, RESERVATION-API-BRIEF §7.4).
//
//   quoteId = base64url(payload) + "." + base64url(HMAC-SHA256(payload, secret))
//
// Stateless: no row per visitor. The payload carries what the guest was
// quoted, so A5's POST can book at that price without re-pricing (D6: a live
// quote books at its price even if the grid moved), after checking the
// signature and the expiry. Web Crypto only, so the same code runs in a CF
// Function and in vitest. The secret never leaves the Function's environment.

import type { DirectQuote } from '@lemontaigu/pms-core';

/** How long a quote books at its price (D6). */
export const QUOTE_TTL_MS = 30 * 60 * 1000;

export interface QuotePayload {
  v: 1;
  quote: DirectQuote;
  issuedAt: string; // RFC3339
  expiresAt: string; // RFC3339
}

export type QuoteVerdict =
  | { ok: true; payload: QuotePayload }
  | { ok: false; reason: 'malformed' | 'bad_signature' | 'expired' };

const enc = new TextEncoder();

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) return null;
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  try {
    const bin = atob(b64);
    const out = new Uint8Array(new ArrayBuffer(bin.length));
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  if (secret.length < 32) throw new Error('quote secret too short (32 characters minimum)');
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ]);
}

/** Sign a quote issued at `now`. */
export async function signQuote(
  quote: DirectQuote,
  secret: string,
  now: Date,
): Promise<{ quoteId: string; expiresAt: string }> {
  const payload: QuotePayload = {
    v: 1,
    quote,
    issuedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + QUOTE_TTL_MS).toISOString(),
  };
  // The SIGNED bytes are exactly the bytes sent: the verifier checks the
  // encoded payload as received, never a re-serialisation of a parsed object.
  const body = toBase64Url(enc.encode(JSON.stringify(payload)));
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(body)));
  return { quoteId: `${body}.${toBase64Url(sig)}`, expiresAt: payload.expiresAt };
}

/** Signature first (constant-time, via subtle.verify), then expiry. */
export async function verifyQuote(quoteId: string, secret: string, now: Date): Promise<QuoteVerdict> {
  const parts = quoteId.split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return { ok: false, reason: 'malformed' };
  const [body, sig] = parts as [string, string];
  const sigBytes = fromBase64Url(sig);
  const bodyBytes = fromBase64Url(body);
  if (sigBytes === null || bodyBytes === null) return { ok: false, reason: 'malformed' };

  const good = await crypto.subtle.verify('HMAC', await hmacKey(secret), sigBytes, enc.encode(body));
  if (!good) return { ok: false, reason: 'bad_signature' };

  let payload: QuotePayload;
  try {
    payload = JSON.parse(new TextDecoder().decode(bodyBytes)) as QuotePayload;
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (payload.v !== 1 || typeof payload.expiresAt !== 'string') return { ok: false, reason: 'malformed' };
  if (now.getTime() >= Date.parse(payload.expiresAt)) return { ok: false, reason: 'expired' };
  return { ok: true, payload };
}
